import React, { useEffect, useState } from 'react';
import { Button, Form, Header, Input, Segment } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { WIRE_FORMAT_OPTIONS } from './ChannelAbilities';

// 渠道变体：在同一 wire format 下选择上游实现。后端用空字符串表示官方上游，
// 但 Semantic 的 Select 对空 value 的处理不可靠（会被当作未选中），所以界面上
// 用 'official' 这个哨兵值，提交时再映射回 ''。
const VARIANT_OFFICIAL = 'official';
const VARIANT_OPTIONS = [
  { key: 'official', text: '官方', value: VARIANT_OFFICIAL },
  { key: 'moma', text: 'MoMA 移动云', value: 'moma' },
  { key: 'shaohua', text: '韶华', value: 'shaohua' },
  { key: 'newtoken', text: 'NewToken', value: 'newtoken' },
  { key: 'pksl', text: 'PKSL', value: 'pksl' },
  { key: 'ynyj', text: '有你有剧', value: 'ynyj' },
  { key: 'baidu', text: '百度', value: 'baidu' },
  { key: 'dx', text: '电信', value: 'dx' }
];

const EditChannel = () => {
  const params = useParams();
  const navigate = useNavigate();
  const channelId = params.id;
  const isEdit = channelId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [fetchingModels, setFetchingModels] = useState(false);
  const handleCancel = () => navigate('/channel');

  const originInputs = {
    name: '',
    key: '',
    base_url: '',
    version: 'v1',
    wire_format: 'openai',
    variant: VARIANT_OFFICIAL,
    access_key: '',
    secret_key: '',
    project_name: '',
    models: [],
    groups: ['default']
  };
  const [batch, setBatch] = useState(false);
  const [inputs, setInputs] = useState(originInputs);
  const [groupOptions, setGroupOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [customModel, setCustomModel] = useState('');

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const loadChannel = async () => {
    let res = await API.get(`/api/channel/${channelId}`);
    const { success, message, data } = res.data;
    if (success) {
      data.models = data.models === '' ? [] : data.models.split(',');
      data.groups = data.group === '' ? [] : data.group.split(',');
      // 后端的空 variant 即官方上游，转成界面用的哨兵值
      data.variant = data.variant ? data.variant : VARIANT_OFFICIAL;
      setInputs(data);
      setModelOptions(data.models.map((m) => ({ key: m, text: m, value: m })));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const fetchUpstreamModels = async () => {
    if (!inputs.base_url) {
      showInfo('请先填写 Base URL');
      return;
    }
    if (!inputs.key) {
      showInfo('请先填写 API Key');
      return;
    }
    setFetchingModels(true);
    try {
      let res = await API.post('/api/channel/fetch_models', {
        base_url: inputs.base_url,
        key: inputs.key,
        version: inputs.version || 'v1'
      });
      const { success, message, data } = res.data;
      if (success && data && data.data) {
        const modelIds = data.data.map((m) => m.id).filter(Boolean);
        const options = modelIds.map((m) => ({ key: m, text: m, value: m }));
        setModelOptions(options);
        setInputs((prev) => ({ ...prev, models: modelIds }));
        showSuccess(`已获取 ${modelIds.length} 个模型`);
      } else {
        showError(message || '获取模型列表失败');
      }
    } catch (error) {
      showError(error.message);
    }
    setFetchingModels(false);
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(res.data.data.map((group) => ({
        key: group,
        text: group,
        value: group
      })));
    } catch (error) {
      showError(error.message);
    }
  };

  useEffect(() => {
    if (isEdit) loadChannel();
    fetchGroups();
  }, []);

  // Keep modelOptions in sync when models list changes externally
  useEffect(() => {
    setModelOptions((prev) => {
      const existing = new Set(prev.map((o) => o.key));
      const extra = inputs.models
        .filter((m) => !existing.has(m))
        .map((m) => ({ key: m, text: m, value: m }));
      return [...prev, ...extra];
    });
  }, [inputs.models]);

  const submit = async () => {
    if (!isEdit && (!inputs.name || !inputs.key || !inputs.base_url)) {
      showInfo('请填写渠道名称、Base URL 和 API Key');
      return;
    }
    let localInputs = { ...inputs };
    if (localInputs.base_url.endsWith('/')) {
      localInputs.base_url = localInputs.base_url.slice(0, -1);
    }
    localInputs.models = localInputs.models.join(',');
    localInputs.group = localInputs.groups.join(',');
    // 官方上游在后端就是空字符串
    if (localInputs.variant === VARIANT_OFFICIAL) {
      localInputs.variant = '';
    }
    let res;
    if (isEdit) {
      res = await API.put(`/api/channel/`, { ...localInputs, id: parseInt(channelId) });
    } else {
      res = await API.post(`/api/channel/`, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(isEdit ? '渠道更新成功！' : '渠道创建成功！');
      if (isEdit) {
        navigate('/channel');
      } else {
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  const addCustomModel = () => {
    if (!customModel.trim()) return;
    if (inputs.models.includes(customModel)) return;
    setModelOptions((prev) => [...prev, { key: customModel, text: customModel, value: customModel }]);
    handleInputChange(null, { name: 'models', value: [...inputs.models, customModel] });
    setCustomModel('');
  };

  return (
    <>
      <Segment loading={loading}>
        <Header as='h3'>{isEdit ? '更新渠道信息' : '创建新的渠道'}</Header>
        <Form autoComplete='new-password'>
          <Form.Field>
            <Form.Input
              label='名称'
              required
              name='name'
              placeholder='请为渠道命名'
              onChange={handleInputChange}
              value={inputs.name}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='Base URL'
              required
              name='base_url'
              placeholder='例如：https://api.openai.com'
              onChange={handleInputChange}
              value={inputs.base_url}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='API Version'
              name='version'
              placeholder='v1'
              onChange={handleInputChange}
              value={inputs.version || 'v1'}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Select
              label='Wire Format'
              name='wire_format'
              options={WIRE_FORMAT_OPTIONS}
              onChange={handleInputChange}
              value={inputs.wire_format || 'openai'}
            />
          </Form.Field>
          <Form.Field>
            <Form.Select
              label='Variant'
              name='variant'
              options={VARIANT_OPTIONS}
              onChange={handleInputChange}
              value={inputs.variant || VARIANT_OFFICIAL}
            />
            <div style={{ marginTop: 4, fontSize: '0.85em', color: '#767676' }}>
              非官方上游（如 MoMA 移动云）走同样的官方 API 格式，仅换后端实现；
              AK/SK 需填对应上游的凭证。
              NewToken / PKSL / 有你有剧 / 百度 / 电信 例外：视频生成用上面的 API Key，素材库用 Access Key
              作为 Bearer token（素材库地址代码里写死），不需要 Secret Key。
            </div>
          </Form.Field>
          <Form.Field>
            {batch ? (
              <Form.TextArea
                label='API Key'
                name='key'
                required
                placeholder='批量创建时请每行输入一个 Key'
                onChange={handleInputChange}
                value={inputs.key}
                style={{ minHeight: 150, fontFamily: 'JetBrains Mono, Consolas' }}
                autoComplete='new-password'
              />
            ) : (
              <Form.Input
                label='API Key'
                name='key'
                required
                placeholder='请输入渠道对应的 API Key'
                onChange={handleInputChange}
                value={inputs.key}
                autoComplete='new-password'
              />
            )}
          </Form.Field>
          {!isEdit && (
            <Form.Checkbox
              checked={batch}
              label='批量创建'
              name='batch'
              onChange={() => setBatch(!batch)}
            />
          )}
          <Form.Field>
            <Form.Input
              label='Access Key (AK)'
              name='access_key'
              placeholder='AK/SK 鉴权渠道（如火山方舟 KYC）才需要填写'
              onChange={handleInputChange}
              value={inputs.access_key || ''}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='Secret Key (SK)'
              name='secret_key'
              type='password'
              placeholder='AK/SK 鉴权渠道（如火山方舟 KYC）才需要填写'
              onChange={handleInputChange}
              value={inputs.secret_key || ''}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='ProjectName'
              name='project_name'
              placeholder='火山方舟项目名，填写后覆盖该渠道所有 KYC 请求的 ProjectName；留空不覆盖'
              onChange={handleInputChange}
              value={inputs.project_name || ''}
            />
          </Form.Field>
          <Form.Field>
            <Form.Dropdown
              label='分组'
              placeholder='请选择可以使用该渠道的分组'
              name='groups'
              fluid
              multiple
              selection
              allowAdditions
              additionLabel='请在系统设置页面编辑分组倍率以添加新的分组：'
              onChange={handleInputChange}
              value={inputs.groups}
              options={groupOptions}
            />
          </Form.Field>
          <Form.Field>
            <Form.Dropdown
              label='模型'
              placeholder='请选择该渠道所支持的模型'
              name='models'
              fluid
              multiple
              selection
              disabled={isEdit}
              onChange={handleInputChange}
              value={inputs.models}
              options={modelOptions}
            />
            {isEdit && (
              <p style={{ color: '#888', fontSize: '0.9em', marginTop: 4 }}>
                模型列表在创建后不可编辑，请通过「能力管理」页面增删模型。
              </p>
            )}
          </Form.Field>
          {!isEdit && (
          <div style={{ lineHeight: '40px', marginBottom: '12px' }}>
            <Button
              type='button'
              loading={fetchingModels}
              onClick={fetchUpstreamModels}
            >
              从上游获取模型
            </Button>
            <Button type='button' onClick={() => handleInputChange(null, { name: 'models', value: [] })}>
              清除所有模型
            </Button>
            <Input
              action={<Button type='button' onClick={addCustomModel}>填入</Button>}
              placeholder='输入自定义模型名称'
              value={customModel}
              onChange={(e, { value }) => setCustomModel(value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { addCustomModel(); e.preventDefault(); }
              }}
            />
          </div>
          )}
          <Button onClick={handleCancel}>取消</Button>
          <Button type={isEdit ? 'button' : 'submit'} positive onClick={submit}>提交</Button>
        </Form>
      </Segment>
    </>
  );
};

export default EditChannel;
